import React, { useEffect, useState } from 'react';
import { Plus, Trash2, History } from 'lucide-react';
import { erpGet } from '../../../services/erpApi';
import { Button, fmtInr } from '../ui';
import { Picker } from './Picker';

export const emptyLine = () => ({ key: Math.random().toString(36).slice(2), sku_id: '', qty: 1, rate: '', discount_pct: 0, gst_rate: 18 });

export const lineMath = (l) => {
  const qty = +l.qty || 0, rate = +l.rate || 0, disc = +l.discount_pct || 0, gst = +l.gst_rate || 0;
  const taxable = qty * rate * (1 - disc / 100);
  const tax = taxable * gst / 100;
  return { taxable, tax, amount: taxable + tax };
};

export const sumLines = (lines) => lines.reduce((a, l) => { const m = lineMath(l); return { taxable: a.taxable + m.taxable, tax: a.tax + m.tax, total: a.total + m.amount }; }, { taxable: 0, tax: 0, total: 0 });

export const LastPrices = ({ customerId, skuId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!customerId || !skuId) { setRows(null); return; }
    erpGet('sales/last-prices', { customer_id: customerId, sku_id: skuId }).then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  }, [customerId, skuId]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px]" data-testid="last-prices">
      <History size={11} style={{ color: 'var(--erp-muted)' }} />
      {rows.map((r) => <span key={r.so_number} className="px-2 py-0.5 rounded-md font-semibold" style={{ background: '#fff4e0', color: '#8a4b00' }} title={`${r.so_number} · ${r.date}`}>₹{r.rate}{r.discount_pct ? ` −${r.discount_pct}%` : ''}</span>)}
      <span style={{ color: 'var(--erp-muted)' }}>last paid</span>
    </div>
  );
};

const NumCell = ({ value, onChange, testid, min = 0, step = 'any', className = '' }) => (
  <input type="number" min={min} step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`erp-input !h-9 text-right ${className}`} />
);

// Editable line items. skus: array from /skus. customerId enables last-price hints. showPrices=false for indents.
export const LineItemsEditor = ({ lines, onChange, skus, customerId, showPrices = true, testid = 'lines' }) => {
  const opts = skus.map((s) => ({ value: s.id, label: s.name, sub: `${s.code} · ${fmtInr(s.list_price)} / ${s.uom}` }));
  const upd = (i, patch) => onChange(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  const pick = (i, id, o) => { const s = skus.find((x) => x.id === id); upd(i, { sku_id: id, rate: lines[i].rate || s?.list_price || '', uom: s?.uom }); };
  const totals = sumLines(lines);
  return (
    <div data-testid={testid}>
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--erp-line)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider" style={{ background: '#f8fafd', color: 'var(--erp-muted)' }}>
              <th className="text-left font-semibold px-3 py-2 w-[40%]">Item</th>
              <th className="text-right font-semibold px-2 py-2 w-24">Qty</th>
              {showPrices && <><th className="text-right font-semibold px-2 py-2 w-28">Rate ₹</th><th className="text-right font-semibold px-2 py-2 w-20">Disc %</th><th className="text-right font-semibold px-2 py-2 w-20">GST %</th><th className="text-right font-semibold px-3 py-2 w-28">Amount</th></>}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.key} className="border-t align-top" style={{ borderColor: 'var(--erp-line)' }} data-testid={`${testid}-row-${i}`}>
                <td className="px-3 py-2">
                  <Picker value={l.sku_id} onChange={(id, o) => pick(i, id, o)} options={opts} placeholder="Select SKU" testid={`${testid}-sku-${i}`} required />
                  {showPrices && <LastPrices customerId={customerId} skuId={l.sku_id} />}
                </td>
                <td className="px-2 py-2"><NumCell value={l.qty} onChange={(v) => upd(i, { qty: v })} testid={`${testid}-qty-${i}`} min={0} /></td>
                {showPrices && <>
                  <td className="px-2 py-2"><NumCell value={l.rate} onChange={(v) => upd(i, { rate: v })} testid={`${testid}-rate-${i}`} /></td>
                  <td className="px-2 py-2"><NumCell value={l.discount_pct} onChange={(v) => upd(i, { discount_pct: v })} testid={`${testid}-disc-${i}`} /></td>
                  <td className="px-2 py-2"><NumCell value={l.gst_rate} onChange={(v) => upd(i, { gst_rate: v })} testid={`${testid}-gst-${i}`} /></td>
                  <td className="px-3 py-2 text-right font-bold pt-4" data-testid={`${testid}-amount-${i}`}>{fmtInr(lineMath(l).amount)}</td>
                </>}
                <td className="py-2 pr-2 pt-3">
                  <button type="button" onClick={() => onChange(lines.filter((_, n) => n !== i))} disabled={lines.length === 1} className="h-8 w-8 rounded-md grid place-items-center text-red-500 hover:bg-red-50 disabled:opacity-30" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-3">
        <Button variant="soft" size="sm" icon={Plus} onClick={() => onChange([...lines, emptyLine()])} data-testid={`${testid}-add`}>Add line</Button>
        {showPrices && (
          <div className="text-sm text-right space-y-0.5 min-w-[220px]" data-testid={`${testid}-totals`}>
            <div className="flex justify-between gap-6"><span style={{ color: 'var(--erp-muted)' }}>Taxable</span><span className="font-semibold">{fmtInr(totals.taxable)}</span></div>
            <div className="flex justify-between gap-6"><span style={{ color: 'var(--erp-muted)' }}>GST</span><span className="font-semibold">{fmtInr(totals.tax)}</span></div>
            <div className="flex justify-between gap-6 text-base pt-1 border-t" style={{ borderColor: 'var(--erp-line)' }}><span className="font-bold">Total</span><span className="font-extrabold" style={{ color: 'var(--erp-navy)' }}>{fmtInr(Math.round(totals.total))}</span></div>
          </div>
        )}
      </div>
    </div>
  );
};

export const linesPayload = (lines) => lines.filter((l) => l.sku_id).map((l) => ({ sku_id: l.sku_id, qty: +l.qty, rate: +l.rate, discount_pct: +l.discount_pct || 0, gst_rate: +l.gst_rate || 0 }));
